import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Lock, Pencil } from 'lucide-react';
import Navbar from '@/components/Navbar';
import GalleryManager from '@/components/GalleryManager';
import { ActivityDataCard } from '@/components/ActivityDataCard';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReadonlyField({ label, value }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">{label}</Label>
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-muted/40 border border-border">
        <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" strokeWidth={1.5} />
        <span className="text-sm font-sans">{value || '—'}</span>
      </div>
    </div>
  );
}

export default function RestaurantProfile() {
  const { t } = useLang();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [fiscalCode, setFiscalCode] = useState('');
  const [docType, setDocType] = useState('');
  const [docNumber, setDocNumber] = useState('');

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API}/business/profile`, { withCredentials: true });
      setProfile(res.data);
      setPhone(res.data.phone_number || '');
      setFirstName(res.data.legal_rep_first_name || '');
      setLastName(res.data.legal_rep_last_name || '');
      setPlaceOfBirth(res.data.legal_rep_place_of_birth || '');
      setDateOfBirth(res.data.legal_rep_date_of_birth || '');
      setFiscalCode(res.data.legal_rep_fiscal_code || '');
      setDocType(res.data.legal_rep_document_type || '');
      setDocNumber(res.data.legal_rep_document_number || '');
    } catch (err) {
      console.error('Failed to load profile', err);
      toast.error('Errore nel caricamento del profilo');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadProfile(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/business/profile`, {
        phone_number: phone,
        legal_rep_first_name: firstName,
        legal_rep_last_name: lastName,
        legal_rep_place_of_birth: placeOfBirth,
        legal_rep_date_of_birth: dateOfBirth,
        legal_rep_fiscal_code: fiscalCode,
        legal_rep_document_type: docType,
        legal_rep_document_number: docNumber,
      }, { withCredentials: true });
      toast.success('Profilo aggiornato');
      setEditing(false);
      loadProfile();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore aggiornamento');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>;

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="restaurant-profile-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        <div className="mb-6 sm:mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2 font-sans">Il tuo profilo</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>La mia attività</h1>
        </div>

        <ActivityDataCard t={t} />

        {/* Gallery vetrina */}
        <Card className="border border-border rounded-2xl mb-6" data-testid="gallery-card">
          <CardContent className="p-5 sm:p-6">
            <div className="mb-3">
              <p className="text-sm font-semibold font-sans">{t('gal_title')}</p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">{t('gal_helper')}</p>
            </div>
            <GalleryManager gallery={profile?.gallery || []} t={t} onChange={loadProfile} />
          </CardContent>
        </Card>

        {/* Business info (readonly) */}
        <Card className="border border-border rounded-2xl mb-6" data-testid="business-info-card">
          <CardContent className="p-5 sm:p-6">
            <p className="text-sm font-semibold font-sans mb-4">{t('cp_company_data')} <span className="text-xs text-muted-foreground font-normal">{t('cp_company_hint')}</span></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ReadonlyField label="Ragione sociale" value={profile?.company_name} />
              <ReadonlyField label="Indirizzo sede legale" value={profile?.registered_address} />
              <ReadonlyField label="Partita IVA" value={profile?.vat_number} />
              <ReadonlyField label="IBAN" value={profile?.iban_masked || profile?.iban} />
              <ReadonlyField label="Categoria" value={profile?.category ? t(`cat_${profile.category}`) : ''} />
            </div>
          </CardContent>
        </Card>

        {/* Editable section */}
        <Card className="border border-border rounded-2xl" data-testid="editable-info-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold font-sans">{t('cp_legal_title')}</p>
              {!editing ? (
                <Button onClick={() => setEditing(true)} variant="outline" size="sm" className="rounded-full" data-testid="edit-btn"><Pencil className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Modifica</Button>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={() => { setEditing(false); loadProfile(); }} variant="ghost" size="sm" className="rounded-full" data-testid="cancel-btn">Annulla</Button>
                  <Button onClick={handleSave} disabled={saving} size="sm" className="rounded-full" data-testid="save-btn"><Save className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> {saving ? 'Salvo...' : 'Salva'}</Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">Telefono</Label>
                <Input value={phone} onChange={e => setPhone(e.target.value)} disabled={!editing} className="rounded-lg h-11" data-testid="input-edit-phone" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">Nome legale rappresentante</Label>
                <Input value={firstName} onChange={e => setFirstName(e.target.value)} disabled={!editing} className="rounded-lg h-11" data-testid="input-edit-first-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">Cognome</Label>
                <Input value={lastName} onChange={e => setLastName(e.target.value)} disabled={!editing} className="rounded-lg h-11" data-testid="input-edit-last-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">Luogo di nascita</Label>
                <Input value={placeOfBirth} onChange={e => setPlaceOfBirth(e.target.value)} disabled={!editing} className="rounded-lg h-11" data-testid="input-edit-birth-place" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">Data di nascita</Label>
                <Input type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} disabled={!editing} className="rounded-lg h-11" data-testid="input-edit-birth-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">Codice fiscale</Label>
                <Input value={fiscalCode} onChange={e => setFiscalCode(e.target.value.toUpperCase())} disabled={!editing} maxLength={16} className="rounded-lg h-11 uppercase" data-testid="input-edit-fiscal-code" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">Tipo documento</Label>
                <Select value={docType} onValueChange={setDocType} disabled={!editing}>
                  <SelectTrigger className="rounded-lg h-11" data-testid="select-edit-doc-type"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carta_identita">Carta d&apos;identità</SelectItem>
                    <SelectItem value="passaporto">Passaporto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">Numero documento</Label>
                <Input value={docNumber} onChange={e => setDocNumber(e.target.value)} disabled={!editing} className="rounded-lg h-11" data-testid="input-edit-doc-number" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
